import { useEffect, useMemo, useState, Children, isValidElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Printer } from 'lucide-react'
import './Help.css'

// The guide lives in public/help/*.md — edit those files to change the content, no code change needed.
// `prefix` makes heading anchors unique across files (e.g. #pages-planner); the "?" links depend on the pages- ids.
const FILES = [
  { file: '00-start-here.md', prefix: 'start' },
  { file: '01-weekly-routine.md', prefix: 'routine' },
  { file: '02-how-to.md', prefix: 'howto' },
  { file: '03-statuses.md', prefix: 'statuses' },
  { file: '04-pages.md', prefix: 'pages' },
  { file: '05-faq.md', prefix: 'faq' },
]

const slugify = text => text.toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

function textOf(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (isValidElement(node)) return textOf(node.props.children)
  return ''
}

// Pull the H1/H2 headings out of a file for the table of contents
function headingsOf(markdown, prefix) {
  const out = []
  let inCode = false
  for (const line of markdown.split('\n')) {
    if (line.startsWith('```')) inCode = !inCode
    const m = !inCode && line.match(/^(#{1,2})\s+(.*)$/)
    if (m) {
      const text = m[2].replace(/[*_`]/g, '').trim()
      out.push({ level: m[1].length, text, id: `${prefix}-${slugify(text)}` })
    }
  }
  return out
}

function scrollToId(id) {
  const el = id && document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' })
}

export default function Help() {
  const [docs, setDocs] = useState(null)
  const [error, setError] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    Promise.all(FILES.map(f =>
      fetch(`/help/${f.file}`, { cache: 'no-cache' }).then(r => {
        if (!r.ok) throw new Error(`Could not load ${f.file}`)
        return r.text()
      }).then(text => ({ ...f, text }))
    ))
      .then(d => { if (!cancelled) setDocs(d) })
      .catch(e => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [])

  // Jump to the section in the URL (from a "?" link or the contents) once the guide has loaded
  useEffect(() => {
    if (!docs || !location.hash) return
    const id = decodeURIComponent(location.hash.slice(1))
    requestAnimationFrame(() => scrollToId(id))
  }, [docs, location.hash])

  const toc = useMemo(() => (docs || []).map(d => ({ prefix: d.prefix, headings: headingsOf(d.text, d.prefix) })), [docs])

  function go(e, id) {
    e.preventDefault()
    navigate({ hash: id }, { replace: true })
    scrollToId(id)
  }

  const componentsFor = prefix => {
    const heading = Tag => ({ children }) => <Tag id={`${prefix}-${slugify(textOf(Children.toArray(children)))}`}>{children}</Tag>
    return {
      h1: heading('h1'),
      h2: heading('h2'),
      h3: heading('h3'),
      table: ({ children }) => <div className="table-wrap"><table>{children}</table></div>,
    }
  }

  return (
    <div className="page help-page">
      <div className="page-header">
        <h1>Help</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary help-print-btn" onClick={() => window.print()}>
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {error && <div className="card">Couldn't load the guide: {error}</div>}
      {!docs && !error && <div className="spinner" />}

      {docs && (
        <div className="help-layout">
          <nav className="help-toc card" aria-label="Contents">
            {toc.map(({ prefix, headings }) => (
              <div key={prefix} className="help-toc-group">
                {headings.map(h => (
                  <a key={h.id} href={`#${h.id}`} onClick={e => go(e, h.id)}
                    className={`help-toc-link level-${h.level}${location.hash === `#${h.id}` ? ' active' : ''}`}>
                    {h.text}
                  </a>
                ))}
              </div>
            ))}
          </nav>

          <div className="help-content">
            {docs.map(d => (
              <section key={d.prefix} className="help-doc card">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentsFor(d.prefix)}>{d.text}</ReactMarkdown>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
